import {
  Component,
  computed,
  inject,
  Inject,
  model,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  SIDE_PANEL_DATA,
  SidePanelData,
} from '@components/abb-side-panel/side-panel';
import { AbbSidePanelComponent } from '@components/abb-side-panel/abb-side-panel.component';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { generateUniqueDomId } from '@utils/dom';
import {
  ContractInput,
  ContractListQuery,
  ContractTagsQuery,
  ContractTagsQueryVariables,
  ContractTypesQuery,
  ContractTypesQueryVariables,
  CreateContractMutation,
  CreateContractMutationVariables,
  DeviceSelectOptionsQuery,
  DeviceSelectOptionsQueryVariables,
  UpdateContractMutation,
  UpdateContractMutationVariables,
} from 'gql/graphql';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import {
  contractTagsQuery,
  contractTypesQuery,
  createContractMutation,
  updateContractMutation,
} from '@graphql/contracts-and-tariffs';
import { MatSelectModule } from '@angular/material/select';
import { deviceSelectOptionsQuery } from '@graphql/asset-management';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AbbTimeInputComponent } from '@components/abb-form/abb-time-picker';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { MatButtonModule } from '@angular/material/button';
import { DayOfWeekPipe } from '@pipes/day-of-week.pipe';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UserSettingsService } from '@services/user/user-settings.service';
import { getErrorMessage } from '@utils/errors';
import { t } from '@utils/strings';

function timeRangeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control || !control.value) {
      return null;
    }

    const fromDate = control.get('validFromDate')?.value;
    const toDate = control.get('validToDate')?.value;
    const fromTime = control.get('validFromTime')?.value;
    const toTime = control.get('validToTime')?.value;


    if (fromDate && toDate) {
      const fromDateTime = new Date(fromDate);
      const toDateTime = new Date(toDate);
      
      if (fromDateTime > toDateTime) {
        return { 
          dateRange: { 
            message: 'End date must be after start date',
            fromDate,
            toDate 
          } 
        };
      }
      
      // Se le date sono uguali, controlliamo gli orari
      if (fromDateTime.getTime() === toDateTime.getTime() && fromTime && toTime) {
        const fromMinutes = timeToMinutes(fromTime);
        const toMinutes = timeToMinutes(toTime);

        if (fromMinutes >= toMinutes) {
          return { 
            timeRange: { 
              message: 'End time must be after start time',
              fromTime,
              toTime 
            } 
          };
        }
      }
    }

    return null;
  };
}

// Converte "HH:MM" in minuti totali
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

type ContractSidePanelData =
  | {
      mode: 'new';
      initialValue?: undefined;
    }
  | {
      mode: 'edit';
      initialValue: ContractListQuery['contractList'][number];
    };

type TariffData = {
  name: string;
  price: number;
  validFromDate: Date | null;
  validToDate: Date | null;
  validDays: Array<number>;
  validFromTime: string;
  validToTime: string;
};

@Component({
  selector: 'abb-contract-side-panel',
  standalone: true,
  imports: [
    AbbSidePanelComponent,
    AbbButtonComponent,
    FormsModule,
    CommonModule,
    TPipe,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatChipsModule,
    AbbIconComponent,
    ReactiveFormsModule,
    MatDatepickerModule,
    AbbTimeInputComponent,
    OverlayModule,
    MatButtonModule,
    DayOfWeekPipe,
  ],
  template: `<abb-side-panel
    [closable]="true"
    [backdropClickClosable]="true"
    [title]="'Contract configuration' | t"
  >
    <form
      class="flex flex-col mb-1 gap-2"
      [id]="formId"
      (ngSubmit)="save()"
      [formGroup]="form"
    >
      <mat-form-field>
        <mat-label>{{ 'Name' | t }}</mat-label>
        <input matInput formControlName="name" />
      </mat-form-field>
      <mat-form-field>
        <mat-label>{{ 'Contract type' | t }}</mat-label>
        <mat-select formControlName="contractType" [panelClass]="'panel'">
          @for (opt of contractTypes$ | async; track opt.id) {
          <mat-option [value]="opt.id">{{ opt.name | t }}</mat-option>
          }
        </mat-select></mat-form-field
      ><mat-form-field>
        <mat-label>{{ 'Tag' | t }}</mat-label>
        <mat-select formControlName="tag" [panelClass]="'panel'">
          @for (opt of contractTags$ | async; track opt.id) {
          <mat-option [value]="opt.id">{{ opt.name | t }}</mat-option>
          }
        </mat-select></mat-form-field
      >
      <mat-form-field>
        <mat-label>{{ 'Devices' | t }}</mat-label>
        <mat-chip-grid #chipGrid>
          @for (el of selectedDevices(); track el) {
          <mat-chip-row (removed)="removeDevice(el)" class="chips">
            {{ el.name }}
            <button matChipRemove type="button">
              <abb-icon size="small" name="mat-outlined/close"></abb-icon>
            </button>
          </mat-chip-row>
          }
        </mat-chip-grid>
        <input
          type="text"
          matInput
          formControlName="deviceQuery"
          [matAutocomplete]="devicesAutocomplete"
          [matChipInputFor]="chipGrid"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          (matChipInputTokenEnd)="onAddDevice($event)"
          #ref
        />
        <mat-autocomplete
          #devicesAutocomplete="matAutocomplete"
          [displayWith]="autocompleteDisplayWith"
          (optionSelected)="onSelectDevice($event, ref)"
          [hideSingleSelectionIndicator]="true"
          class="panel"
        >
          @for (el of filteredDevices(); track el.id) {
          <mat-option [value]="el">{{ el.name }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
      <h6 class="font-bold text-lg my-2">{{ 'Tariffs' | t }}</h6>
      <div formGroupName="tariffs">
        @for (tariff of tariffsArray(); track tariff.id) {
        <div [formGroupName]="tariff.id" class="flex flex-col gap-2">
          <div class="flex justify-between gap-x-4 items-center">
            <mat-form-field class="flex-1">
              <mat-label>{{ 'Name' | t }}</mat-label>
              <input matInput formControlName="name" />
            </mat-form-field>
            <abb-button
              class="mb-[22px]"
              type="button"
              variant="clean"
              (onclick)="removeTariff(tariff.id)"
              >{{ 'Remove tariff' | t }}</abb-button
            >
          </div>
          <mat-form-field>
            <mat-label>{{ 'Cost' | t }}</mat-label>
            <input matInput formControlName="price" type="number" step="0.01" />
          </mat-form-field>
          <div class="flex gap-x-4">
            <mat-form-field>
              <mat-label>{{ 'From' | t }}</mat-label>
              <input
                matInput
                [matDatepicker]="fromPicker"
                formControlName="validFromDate"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="fromPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #fromPicker [panelClass]="'panel'">
                <mat-datepicker-actions>
                  <div class="!flex !flex-col !w-full">
                    <div class="flex flex-row w-full gap-5 p-5 justify-end">
                      <abb-button
                        [variant]="'clean'"
                        matDateRangePickerCancel
                        >{{ 'cancel' | t }}</abb-button
                      >
                      <abb-button matDateRangePickerApply>{{
                        'Apply' | t
                      }}</abb-button>
                    </div>
                  </div>
                </mat-datepicker-actions>
              </mat-datepicker>
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'To' | t }}</mat-label>
              <input
                matInput
                [matDatepicker]="toPicker"
                formControlName="validToDate"
              />
              <mat-datepicker-toggle
                matIconSuffix
                [for]="toPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #toPicker [panelClass]="'panel'">
                <mat-datepicker-actions>
                  <div class="!flex !flex-col !w-full">
                    <div class="flex flex-row w-full gap-5 p-5 justify-end">
                      <abb-button
                        [variant]="'clean'"
                        matDateRangePickerCancel
                        >{{ 'cancel' | t }}</abb-button
                      >
                      <abb-button matDateRangePickerApply>{{
                        'Apply' | t
                      }}</abb-button>
                    </div>
                  </div>
                </mat-datepicker-actions>
              </mat-datepicker>
              @if(timezone()){
              <mat-hint
                [align]="'end'"
                class="inline-flex items-center text-sm text-abb-font/65"
                >{{ '(' + timezone() + ')' }}</mat-hint
              >}
            </mat-form-field>
          </div>
          <div class="flex gap-x-4">
            <ng-container
              *ngTemplateOutlet="
                timePickerTemplate;
                context: {
                  $implicit: {
                    tariff,
                    label: ('From time' | t),
                    formControlName: 'validFromTime',
                    formControlNamePopup: 'validFromTimePopup',
                    name: 'from'
                  }
                }
              "
            ></ng-container>
            <ng-container
              *ngTemplateOutlet="
                timePickerTemplate;
                context: {
                  $implicit: {
                    tariff,
                    label: ('To time' | t),
                    formControlName: 'validToTime',
                    formControlNamePopup: 'validToTimePopup',
                    name: 'to'
                  }
                }
              "
            ></ng-container>

            <ng-template #timePickerTemplate let-data>
              <mat-form-field
                ><mat-label>{{ data.label }}</mat-label>
                <input
                  matInput
                  type="time"
                  [formControlName]="data.formControlName"
                />
                <button
                  mat-icon-button
                  type="button"
                  matIconSuffix
                  (click)="toggleOverlay(data.tariff.id, data.name)"
                  cdkOverlayOrigin
                  #trigger="cdkOverlayOrigin"
                >
                  <abb-icon
                    name="mat/schedule"
                    class="text-[var(--mat-datepicker-toggle-icon-color)]"
                  ></abb-icon>
                </button>
                <ng-template
                  cdkConnectedOverlay
                  [cdkConnectedOverlayOrigin]="trigger"
                  [cdkConnectedOverlayBackdropClass]="'opacity-0'"
                  [cdkConnectedOverlayOpen]="
                    openTimeOverlay() === data.name + data.tariff.id
                  "
                  [cdkConnectedOverlayHasBackdrop]="true"
                  [cdkConnectedOverlayPositions]="overlayPositions"
                  (backdropClick)="toggleOverlay(data.tariff.id, data.name)"
                  ><div class="bg-abb-background shadow rounded-sm">
                    <abb-time-input
                      [formControlName]="data.formControlNamePopup"
                    ></abb-time-input></div
                ></ng-template>
              </mat-form-field>
            </ng-template>
          </div>
          <!-- Messaggi di errore per validazione date e orari -->
          @if (form.controls.tariffs.get(tariff.id)?.hasError('dateRange')) {
          <div class="text-red-500 text-sm mt-1">
            {{ 'End date must be after start date' | t }}
          </div>
          }
          @if (form.controls.tariffs.get(tariff.id)?.hasError('timeRange')) {
          <div class="text-red-500 text-sm mt-1">
            {{ 'End time must be after start time' | t }}
          </div>
          }
          <mat-form-field>
            <mat-label>{{ 'Days' | t }}</mat-label>
            <mat-select
              [multiple]="true"
              formControlName="validDays"
              [panelClass]="'panel border-font-checkbox'"
            >
              @for(day of days; track day) {
              <mat-option [value]="day">{{ day | dayOfWeek }}</mat-option>
              }</mat-select
            >
          </mat-form-field>
        </div>
        @if (!$last) {
        <hr class="border-abb-black/20 mb-5" />
        } } @if (tariffsArray().length === 0) {
        <p class="text-abb-font/60 mb-2">{{ 'No tariffs found' | t }}</p>
        }
        <abb-button variant="secondary" type="button" (onclick)="addTariff()">{{
          'New tariff' | t
        }}</abb-button>
      </div>
    </form>
    <div footer class="w-full flex flex-row gap-8 justify-end p-8">
      <abb-button
        variant="clean"
        [disabled]="saving()"
        (onclick)="onCancel()"
        >{{ 'cancel' | t }}</abb-button
      >
      <abb-button
        class="w-32"
        type="submit"
        [form]="formId"
        [loading]="saving()"
        [disabled]="form.invalid || saving() || tariffsArray().length === 0"
        >{{ (data.mode === 'edit' ? 'Save' : 'Add contract') | t }}</abb-button
      >
    </div>
  </abb-side-panel> `,
  styles: ``,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'property_services.tabs.costs_tariffs.side_panel',
    },
  ],
})
export class ContractSidePanelComponent
  implements SidePanelData<ContractSidePanelData>, OnInit, OnDestroy
{
  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];
  readonly days = [0, 1, 2, 3, 4, 5, 6];
  prefix = inject(TRANSLATION_PREFIX);
  formId = generateUniqueDomId('contract-side-panel');
  panelRef = viewChild(AbbSidePanelComponent);
  contractName = model<string>('');
  contractTag = model<string | null>(null);
  contractType = model<string | null>(null);
  contractTypes$ = new BehaviorSubject<ContractTypesQuery['contractTypesList']>(
    []
  );
  contractTags$ = new BehaviorSubject<ContractTagsQuery['contractTagsList']>(
    []
  );
  devices = signal<DeviceSelectOptionsQuery['deviceList']>([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  selectedDevices = signal<DeviceSelectOptionsQuery['deviceList']>([]);
  deviceQuery = signal('');
  openTimeOverlay = signal<string | null>(null);
  private localIdCounter = 0;
  filteredDevices = computed(() => {
    const deviceQuery = this.deviceQuery();
    return this.devices().filter(
      (d) =>
        d.name.toLowerCase().includes(deviceQuery) &&
        !this.selectedDevices().some((sel) => sel.id === d.id)
    );
  });
  tariffs = signal<Record<string, TariffData>>({});
  tariffsArray = computed(() => {
    return Object.entries(this.tariffs()).map(([id, data]) => ({
      id,
      ...data,
    }));
  });
  timezone = signal<string>('');
  // TODO: verificare validazione
  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    tag: new FormControl<string | null>(null, [Validators.required]),
    contractType: new FormControl<string | null>(null, [Validators.required]),
    deviceQuery: new FormControl(''),
    tariffs: new FormRecord(
      {} as {
        [id: string]: FormGroup<{
          name: FormControl<string | null>;
          price: FormControl<number | null>;
          validFromDate: FormControl<Date | null>;
          validToDate: FormControl<Date | null>;
          validFromTime: FormControl<string | null>;
          validToTime: FormControl<string | null>;
          validFromTimePopup: FormControl<string | null>;
          validToTimePopup: FormControl<string | null>;
          validDays: FormControl<number[] | null>;
        }>;
      }
    ),
  });
  private formSubscriptions: Record<string, Subscription> = {};
  private subscriptions: Subscription[] = [];
  constructor(
    @Inject(SIDE_PANEL_DATA) public data: ContractSidePanelData,
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly userService: UserSettingsService
  ) {
    this.timezone.set(userService.timezone());
    this.subscriptions.push(
      apollo
        .watchQuery<ContractTypesQuery, ContractTypesQueryVariables>({
          query: contractTypesQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.contractTypes$.next(data.contractTypesList);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<ContractTagsQuery, ContractTagsQueryVariables>({
          query: contractTagsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.contractTags$.next(data.contractTagsList);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),

      apollo
        .watchQuery<
          DeviceSelectOptionsQuery,
          DeviceSelectOptionsQueryVariables
        >({
          query: deviceSelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.devices.set(data.deviceList);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        }),

      this.form.controls.deviceQuery.valueChanges.subscribe((q) => {
        if (typeof q === 'string') {
          this.deviceQuery.set(q?.toLowerCase() ?? '');
        } else {
          this.deviceQuery.set('');
        }
      })
    );
  }

  ngOnInit(): void {
    if (this.data.mode === 'edit') {
      this.data.initialValue.tariffs.forEach((tariff) => {
        this.addTariff(tariff.id, tariff);
      });
      this.selectedDevices.set(
        this.data.initialValue.devices.map(device => ({
          __typename: 'Device' as const,
          id: device.id,
          name: device.name
        }))
      );
      this.form.patchValue({
        name: this.data.initialValue.name,
        deviceQuery: '',
      });
      this.subscriptions.push(
        combineLatest([
          this.contractTypes$.asObservable(),
          this.contractTags$.asObservable(),
        ]).subscribe(([contracts, tags]) => {
          this.form.patchValue({
            contractType: contracts.find(
              (c) => c.id === this.data.initialValue?.type.id
            )?.id,
            tag: tags.find((t) => t.id === this.data.initialValue?.tag.id)?.id,
          });
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    Object.values(this.formSubscriptions).forEach((s) => s.unsubscribe());
  }
  onCancel() {
    this.panelRef()?.dispose();
  }

  saving = signal(false);
  save() {
    if (!this.form.valid) {
      this.toast.notifyWarning(
        t(this.prefix, `Some fields are not valid`, this.translate)
      );
      return;
    }
    this.saving.set(true);
    const contractInput: ContractInput = {
      deviceIds: this.selectedDevices().map((d) => d.id),
      name: this.form.value.name ?? '',
      tagId: this.form.value.tag ?? '',
      typeId: this.form.value.contractType ?? '',
      tariffs: Object.entries(this.form.value.tariffs ?? {}).map(
        ([id, tariff]) => {
          const data = {
            name: tariff?.name ?? '',
            price: tariff?.price ?? 0,
            validDays: tariff?.validDays ?? [],
            validFromDate: tariff?.validFromDate ?? new Date(),
            validFromTime: tariff?.validFromTime ?? '',
            validToDate: tariff?.validToDate ?? new Date(),
            validToTime: tariff?.validToTime ?? '',
          };
          if (id.match(/^local-\d+$/g)) {
            return data;
          }
          return {
            id,
            ...data,
          };
        }
      ),
    };
    if (this.data.mode === 'new') {
      this.apollo
        .mutate<CreateContractMutation, CreateContractMutationVariables>({
          mutation: createContractMutation,
          variables: {
            contract: contractInput,
          },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `Contract created`, this.translate)
            );
            this.apollo.client.refetchQueries({
              include: ['ContractList'],
            });
            this.panelRef()?.dispose();
          },
          error: () => {
            this.saving.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while creating contract`,
                this.translate
              )
            );
          },
        });
    } else {
      this.apollo
        .mutate<UpdateContractMutation, UpdateContractMutationVariables>({
          mutation: updateContractMutation,
          variables: {
            id: this.data.initialValue.id,
            contract: contractInput,
          },
        })
        .subscribe({
          next: () => {
            this.saving.set(false);
            this.toast.notifySuccess(
              t(this.prefix, `Contract updated`, this.translate)
            );
            this.apollo.client.refetchQueries({
              include: ['ContractList'],
            });
            this.panelRef()?.dispose();
          },
          error: () => {
            this.saving.set(false);
            this.toast.notifyError(
              t(
                this.prefix,
                `An error occurred while updating contract`,
                this.translate
              )
            );
          },
        });
    }
  }
  onAddDevice(event: MatChipInputEvent): void {
    const value = (event.value || '').trim().toLowerCase();
    const match = this.filteredDevices().find(
      (a) => a.name.toLowerCase() === value
    );
    if (match) {
      const previousValue = this.selectedDevices();
      this.selectedDevices.update((prev) => {
        if (!prev.some((a) => a.id === match.id)) {
          return [...prev, match];
        }
        return prev;
      });
      if (previousValue === this.selectedDevices()) {
        // value was already selected
        return;
      }
      event.chipInput?.clear();
      this.form.controls.deviceQuery.setValue('');
    }
  }
  removeDevice(item: { id: string }) {
    this.selectedDevices.update((prev) => prev.filter((a) => a.id !== item.id));
  }
  onSelectDevice(
    event: MatAutocompleteSelectedEvent,
    autocompleteRef: HTMLInputElement
  ): void {
    this.selectedDevices.update((prev) => {
      if (!prev.some((a) => a.id === event.option.value.id)) {
        return [...prev, event.option.value];
      }
      return prev.filter((a) => a.id !== event.option.value.id);
    });
    autocompleteRef.value = '';
    this.form.controls.deviceQuery.setValue('');
  }

  autocompleteDisplayWith(asset: any) {
    return asset?.name ?? '';
  }

  toggleOverlay(id: string, picker: 'from' | 'to') {
    if (this.openTimeOverlay() === picker + id) {
      this.openTimeOverlay.set(null);
    } else {
      this.openTimeOverlay.set(picker + id);
    }
  }

  removeTariff(id: string) {
    this.tariffs.update((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    this.form.controls.tariffs.removeControl(id);
    this.formSubscriptions[id].unsubscribe();
    delete this.formSubscriptions[id];
  }

  private getLocalId() {
    return `local-${this.localIdCounter++}`;
  }
  private getDefaultTariffData(): TariffData {
    return {
      name: '',
      price: 0,
      validDays: [],
      validFromDate: null,
      validFromTime: '00:00',
      validToDate: null,
      validToTime: '23:59', // Cambiato da '00:00' a '23:59'
    };
  }

  addTariff(
    id: string = this.getLocalId(),
    tariff: TariffData = this.getDefaultTariffData()
  ) {
    const validFromTime = new FormControl(tariff.validFromTime, [
      Validators.required,
    ]);
    const validToTime = new FormControl(tariff.validToTime, [
      Validators.required,
    ]);
    const validFromTimePopup = new FormControl(tariff.validFromTime);
    const validToTimePopup = new FormControl(tariff.validToTime);
    this.formSubscriptions[id] = new Subscription();
    this.formSubscriptions[id].add(
      validFromTime.valueChanges.subscribe((t) =>
        validFromTimePopup.setValue(t, { emitEvent: false })
      )
    );
    this.formSubscriptions[id].add(
      validToTime.valueChanges.subscribe((t) =>
        validToTimePopup.setValue(t, { emitEvent: false })
      )
    );
    this.formSubscriptions[id].add(
      validFromTimePopup.valueChanges.subscribe((t) =>
        validFromTime.setValue(t, { emitEvent: false })
      )
    );
    this.formSubscriptions[id].add(
      validToTimePopup.valueChanges.subscribe((t) =>
        validToTime.setValue(t, { emitEvent: false })
      )
    );
    this.form.controls.tariffs.addControl(
      id,
      new FormGroup({
        name: new FormControl(tariff.name, [Validators.required]),
        price: new FormControl(tariff.price, [Validators.required]),
        validFromDate: new FormControl(tariff.validFromDate, [
          Validators.required,
        ]),
        validToDate: new FormControl(tariff.validToDate, [Validators.required]),
        validFromTime,
        validToTime,
        validFromTimePopup,
        validToTimePopup,
        validDays: new FormControl(tariff.validDays, [Validators.required]),
      }, { validators: [timeRangeValidator()] }) 
    );
    this.tariffs.update((prev) => {
      return { ...prev, [id]: tariff };
    });
  }
}
